import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiFetch, ApiError } from '../lib/api';

/**
 * Auth provider. The browser holds no Supabase keys or tokens — the session
 * lives in an HttpOnly cookie set by the Worker. This provider's only job is
 * to keep React state in sync with what /api/auth/me reports.
 */

export interface User {
  id: string;
  email?: string;
  app_metadata?: Record<string, unknown>;
  user_metadata?: Record<string, unknown>;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  async function loadUser() {
    try {
      const { user } = await apiFetch<{ user: User }>('/api/auth/me');
      setUser(user);
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 401) {
        // Network or unexpected error — surface so the user knows something's off.
        console.error('Failed to load user', err);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  async function completeHashSession(hash: string) {
    const params = new URLSearchParams(hash.replace(/^#/, ''));
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const type = params.get('type');

    if (!accessToken || !refreshToken) {
      // Supabase signalled an error in the fragment (e.g. expired link).
      window.location.replace('/login?error=expired_link');
      return;
    }

    try {
      await apiFetch('/api/auth/session-from-tokens', {
        method: 'POST',
        body: JSON.stringify({
          access_token: accessToken,
          refresh_token: refreshToken,
          expires_at: params.get('expires_at'),
          expires_in: params.get('expires_in'),
        }),
      });
      const target =
        type === 'recovery'
          ? '/reset-password'
          : type === 'invite' || type === 'signup'
            ? '/accept-invite'
            : '/dashboard';
      // Full navigation to the clean path: drops the fragment from the URL and
      // re-runs the provider, which now finds a valid cookie via /api/auth/me.
      window.location.replace(target);
    } catch {
      window.location.replace('/login?error=expired_link');
    }
  }

  useEffect(() => {
    // Supabase's default (implicit-flow) email links land here with the session
    // in the URL fragment, e.g. `#access_token=…&refresh_token=…&type=recovery`.
    // Hand those tokens to the Worker (which sets the HttpOnly cookie), scrub
    // the fragment, then route to the page that collects the new password.
    const hash = window.location.hash;
    if (hash.includes('access_token=') || hash.includes('error=')) {
      completeHashSession(hash);
      return;
    }
    loadUser();
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      async signIn(email, password) {
        const { user } = await apiFetch<{ user: User }>('/api/auth/login', {
          method: 'POST',
          body: JSON.stringify({ email, password }),
        });
        setUser(user);
      },
      async signOut() {
        await apiFetch('/api/auth/logout', { method: 'POST' });
        setUser(null);
      },
      async sendPasswordReset(email) {
        await apiFetch('/api/auth/forgot-password', {
          method: 'POST',
          body: JSON.stringify({ email }),
        });
      },
      async updatePassword(newPassword) {
        await apiFetch('/api/auth/update-password', {
          method: 'POST',
          body: JSON.stringify({ password: newPassword }),
        });
      },
      refresh: loadUser,
    }),
    [user, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
